import { Formik } from "formik";
import React, { useContext, useState } from "react";
import { Form, Modal } from "react-bootstrap";
import { Button,  ThemeProviderV2} from '@blueprint/ui';
import * as yup from "yup";

import paymentApi from "../../../api/payment";
import { Loader, toast } from "../../../common/components/core";
import { TextareaField } from "../../../common/components/form";
import {
    useAuthErrorHandlerCreator,
    useModalWithData,
} from "../../../common/hooks";
import { FormSubmissionHandler } from "../../../types/common";
import {
    composeErrorHandlers,
    GenericError,
    onStaleStateError,
} from "../../../utils/errorHandling";
import InvoiceContext from "../../invoice/InvoiceContext";

export type DirectorRejectPaymentFormValues = {
    comment: string;
};

const schema: yup.SchemaOf<DirectorRejectPaymentFormValues> = yup
    .object()
    .shape({
        comment: yup.string().default(""),
    });

type RejectPaymentHandler = {
    isSubmitting: boolean;
    handler: (preSubmitPaymentLinearId: string) => void;
};

const useDirectorRejectPaymentHandler = (): RejectPaymentHandler => {
    const [isSubmitting, setSubmitting] = useState(false);
    const { mutate } = useContext(InvoiceContext);
    const onAuthError = useAuthErrorHandlerCreator();

    const [show, hide] = useModalWithData<string>(
        (preSubmitPaymentLinearId) => () => {
            const confirm: FormSubmissionHandler<
                DirectorRejectPaymentFormValues
            > = async (values) => {
                if (!preSubmitPaymentLinearId) {
                    return;
                }
                setSubmitting(true);
                try {
                    const preSubmitPayment =
                        await paymentApi.getPreSubmitPayment(
                            preSubmitPaymentLinearId,
                        );
                    await paymentApi.directorReject(
                        preSubmitPayment.ref,
                        values,
                    );
                    mutate();
                    toast("Payment order rejected");
                    hide();
                } catch (e) {
                    composeErrorHandlers(
                        onStaleStateError(() => {
                            mutate();
                            hide();
                            toast("Page has been updated by another user.");
                        }),
                        onAuthError,
                    )(e as GenericError);
                }
                setSubmitting(false);
            };

            return (
                <ThemeProviderV2>
                <Modal show onHide={hide} backdrop="static" centered>
                    <Formik
                        validationSchema={schema}
                        validateOnBlur={false}
                        validateOnChange={false}
                        onSubmit={confirm}
                        initialValues={{
                            comment: "",
                        }}
                    >
                        {({ handleSubmit }) => (
                            <Loader overlay isLoading={isSubmitting}>
                                <Form
                                    autoComplete="off"
                                    noValidate
                                    onSubmit={handleSubmit}
                                >
                                    <Modal.Header>
                                        <h2 className="mb-0">
                                            Reject Payment Order
                                        </h2>
                                    </Modal.Header>
                                    <Modal.Body>
                                        <p>
                                            Are you sure you want to reject this
                                            payment order?
                                        </p>
                                        <TextareaField
                                            name="comment"
                                            label="Comment (Optional)"
                                            maxLength={256}
                                            isAllowed={(formattedValue) => formattedValue.length < 256}
                                        />
                                    </Modal.Body>
                                    <Modal.Footer className="d-flex justify-content-between px-3">
                                        <Button
                                            variant="light.tertiary"
                                            onClick={hide}
                                        >
                                            Cancel
                                        </Button>
                                        <Button variant="light.primary" type="submit">
                                            Confirm
                                        </Button>
                                    </Modal.Footer>
                                </Form>
                            </Loader>
                        )}
                    </Formik>
                </Modal>
                </ThemeProviderV2>
            );
        },
        [onAuthError, isSubmitting],
    );

    return {
        isSubmitting,
        handler: show,
    };
};

export default useDirectorRejectPaymentHandler;
